// Fact keys, event types and adapter ids (SPEC §4.6, §6.6). Who owns each is in
// apps/api/src/sources/registry.ts and docs/FIELD_REGISTRY.md.

/** Every source adapter (M1). `seed` may provide any key until a real source observes it. */
export const ADAPTER_IDS = [
  'alts-forever',
  'auctionator',
  'skillup-forever',
  'dungeon-journal',
  'forever-map-notes',
  'questie',
  'books-on-map',
  'rarescanner',
  'brimstone',
  'client',
  // Screenshots from the game folder (M12): the image and when it was taken.
  'screenshots',
  // I Remember You (M13): notes and moods on other players.
  'i-remember-you',
  'seed',
] as const;
export type AdapterId = (typeof ADAPTER_IDS)[number];

/** Facts about one character. */
export const CHARACTER_FACT_KEYS = [
  'identity.name',
  'identity.realm',
  'identity.race',
  'identity.class',
  'identity.faction',
  'identity.level',
  'xp.current',
  'xp.max',
  'rest.xp',
  'rest.isResting',
  'session.lastLogoutAt',
  'location.zone',
  'location.subzone',
  'location.hearth',
  'location.position',
  'location.hearthPosition',
  'travel.knownFlightPaths',
  'money.copper',
  'inventory.bags',
  'inventory.bank',
  'inventory.mail',
  'inventory.mail.lastOpenedAt',
  'inventory.equipped',
  'professions',
  'professions.specializations',
  'professions.knownRecipes',
  'cooldowns',
  'lockouts',
  'talents.unspent',
  'talents.specs',
  'quests.log',
  'played.total',
  'played.level',
  'exploration.areas',
  'pets',
  'trainer.lastVisitLevel',
  'mail.outgoing',
  'mail.hasNew',
  'books.collected',
  'legacy.spent',
  'legacy.wellRestedRank',
  // The roster plan (seed/roster.json): only the seed writes these, and nothing replaces them.
  'plan.specs',
  'plan.professions',
  'plan.secondary',
  'plan.pets',
  'plan.notes',
] as const;

/** Facts about the account (no character). */
export const ACCOUNT_FACT_KEYS = [
  'legacy.pointsEarned',
  'legacy.milestonesClaimed',
  'legacy.rewardsUnlocked',
  // Notes on other players (M13), by their full name.
  'people.notes',
] as const;

export const FACT_KEYS = [...CHARACTER_FACT_KEYS, ...ACCOUNT_FACT_KEYS] as const;
export type CharacterFactKey = (typeof CHARACTER_FACT_KEYS)[number];
export type AccountFactKey = (typeof ACCOUNT_FACT_KEYS)[number];
export type FactKey = (typeof FACT_KEYS)[number];

/** Chronicle event types (SPEC §6.6). */
export const CHRONICLE_EVENT_TYPES = [
  'level_up',
  'area_discovered',
  'zone_first_visit',
  'rare_killed',
  'pet_tamed',
  'book_read',
  'profession_rank',
  'profession_specialized',
  'dungeon_cleared',
  'death',
  'epic_loot',
  'legacy_point',
  'legacy_reward',
] as const;
export type ChronicleEventType = (typeof CHRONICLE_EVENT_TYPES)[number];

/** Market and static data, which aren't facts about a character or the account. */
export const PRICE_KEYS = [
  'prices.auction',
  // Daily lows, highs and listings from Auctionator's history (M14).
  'prices.auctionHistory',
  'prices.vendorSell',
  // What vendors charge for trade goods (M14).
  'prices.vendorBuy',
] as const;
export type PriceKey = (typeof PRICE_KEYS)[number];

export const STATIC_KEYS = [
  'static.recipeSources',
  'static.zoneAreas',
  'static.bookNames',
  // Every item and recipe of the client build (M14).
  'static.items',
  'static.recipes',
  'static.map',
  'static.flightMasters',
  'static.bookLocations',
  'static.rares',
  'static.gatherNodes',
  'static.gatherTypes',
  'static.fishingPools',
  'static.dungeonEntrances',
  'static.dungeonMaps',
  'static.dungeons',
  'static.bossLevels',
  'static.questGivers',
] as const;
export type StaticKey = (typeof STATIC_KEYS)[number];
